import React, { useState, useEffect, useRef } from 'react';
import { resolveApiBase } from '@/utils/apiBase';
import { X, Loader2, Shield, Eye, EyeOff, CheckCircle, AlertTriangle, KeyRound, RefreshCw, Save } from 'lucide-react';
import { CvcrmAuthStatus } from '@/types';

interface CvcrmConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  darkMode: boolean;
  currentStatus: CvcrmAuthStatus | null;
  onSuccess: () => void;
  tenantName?: string;
  browserApiBase?: string;
}

interface SavedCredentials {
  email: string;
  senha_configurada: boolean;
  url: string;
  secureConfig: boolean;
}

const CORE_API = resolveApiBase(import.meta.env.VITE_CORE_API, '/core', 'http://localhost:3001');
const DEFAULT_BROWSER_API = resolveApiBase(import.meta.env.VITE_BROWSER_API, '/browser', 'http://localhost:3010');
const DEFAULT_CVCRM_URL = 'https://alphaville.cvcrm.com.br/gestor/';

const getAuthHeaders = (): Record<string, string> => {
  const token = (typeof window !== 'undefined' && localStorage.getItem('va_token')) || '';
  return token ? { Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}` } : {};
};

type Step = 'loading' | 'browser_offline' | 'starting_browser' | 'credentials' | 'otp';

const normalizeBrowserBase = (base?: string): string => {
  if (!base) return DEFAULT_BROWSER_API;
  if (base.toString().startsWith('http') || base.toString().startsWith('/')) return base;
  return import.meta.env.PROD ? '/browser' : `http://localhost:${base}`;
};

// ============================================
// OTP Input Component (6 boxes)
// ============================================
interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void; // Chamado ao apertar Enter
  darkMode: boolean;
  disabled?: boolean;
}

const OtpInput: React.FC<OtpInputProps> = ({ value, onChange, onSubmit, darkMode, disabled }) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = value.padEnd(6, '').split('').slice(0, 6);

  const handleChange = (index: number, digit: string) => {
    if (!/^\d?$/.test(digit)) return;
    
    const newDigits = [...digits];
    newDigits[index] = digit;
    const newValue = newDigits.join('').replace(/\s/g, '');
    onChange(newValue);
    
    // Auto-focus next input
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    
    // Se completou 6 d?gitos e tem onSubmit, chama automaticamente
    if (newValue.length === 6 && onSubmit) {
      setTimeout(() => onSubmit(), 100);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    // Enter = submit
    if (e.key === 'Enter' && value.length === 6 && onSubmit) {
      e.preventDefault();
      onSubmit();
      return;
    }
    
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    onChange(pasted);
    if (pasted.length === 6) {
      inputRefs.current[5]?.focus();
      // Auto-submit ap?s colar c?digo completo
      if (onSubmit) {
        setTimeout(() => onSubmit(), 100);
      }
    } else {
      inputRefs.current[pasted.length]?.focus();
    }
  };

  return (
    <div className="flex gap-2 justify-center">
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <input
          key={index}
          ref={(el) => { inputRefs.current[index] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[index] || ''}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          className={`
            w-12 h-14 text-center text-2xl font-mono font-bold rounded-xl border-2 
            transition-all duration-200 outline-none
            ${darkMode 
              ? 'bg-vision-surface border-vision-border text-white focus:border-blue-500 focus:bg-blue-900/20' 
              : 'bg-white border-gray-200 text-gray-900 focus:border-blue-500 focus:bg-blue-50'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        />
      ))}
    </div>
  );
};

// ============================================
// Main Modal Component
// ============================================
export const CvcrmConfigModal: React.FC<CvcrmConfigModalProps> = ({
  isOpen,
  onClose,
  darkMode,
  onSuccess,
  tenantName,
  browserApiBase: externalBrowserApiBase,
}) => {
  const defaultHeadless = import.meta.env.PROD;
  const [step, setStep] = useState<Step>('loading');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [browserApiBase, setBrowserApiBase] = useState(externalBrowserApiBase || DEFAULT_BROWSER_API);
  
  // Credenciais salvas
  const [savedCredentials, setSavedCredentials] = useState<SavedCredentials | null>(null);
  const [useSavedPassword, setUseSavedPassword] = useState(false);
  const [credentialsChanged, setCredentialsChanged] = useState(false);
  
  // Flag para evitar re-inicializa??o
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (externalBrowserApiBase) setBrowserApiBase(externalBrowserApiBase);
  }, [externalBrowserApiBase]);

  // Tenant resolvido dinamicamente
  const resolvedTenant =
    import.meta.env.VITE_TENANT_ID ||
    import.meta.env.VITE_TENANT_NAME ||
    tenantName ||
    'default';

  // Descobre porta/URL do browser a partir do Core
  const resolveBrowserApi = async () => {
    if (externalBrowserApiBase) {
      setBrowserApiBase(externalBrowserApiBase);
      return externalBrowserApiBase;
    }
    try {
      const res = await fetch(`${CORE_API}/services/ports`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        const port = data?.ports?.browser;
        if (port) {
          const base = import.meta.env.PROD ? '/browser' : `http://localhost:${port}`;
          setBrowserApiBase(base);
          return base;
        }
      }
    } catch (err) {
      console.warn('Nao foi possivel descobrir porta do browser via Core', err);
    }
    setBrowserApiBase(DEFAULT_BROWSER_API);
    return DEFAULT_BROWSER_API;
  };

  // Busca credenciais salvas
  const fetchSavedCredentials = async () => {
    try {
      const response = await fetch(`${browserApiBase}/credentials`);
      if (response.ok) {
        const data = await response.json();
        setSavedCredentials(data);
        setEmail(data.email || '');
        setUseSavedPassword(Boolean(data.senha_configurada));
        return data;
      }
    } catch (err) {
      console.error('Erro ao buscar credenciais:', err);
    }
    return null;
  };

  // Verifica status atual da autentica??o
  const checkAuthStatus = async (): Promise<string | null> => {
    try {
      const response = await fetch(`${browserApiBase}/status?ts=${Date.now()}`, { cache: 'no-store' });
      if (response.ok) {
        const data = await response.json();
        return data.auth?.status || null;
      }
    } catch (err) {
      console.error('Erro ao verificar status:', err);
    }
    return null;
  };

  // Verifica se o Browser API est? acess?vel E se o Puppeteer est? rodando
  const checkBrowserService = async (base?: string): Promise<{ serviceOnline: boolean; browserRunning: boolean }> => {
    const apiBase = base || browserApiBase;
    try {
      const response = await fetch(`${apiBase}/status?ts=${Date.now()}`, { 
        method: 'GET',
        signal: AbortSignal.timeout(5000), // 5s timeout
        cache: 'no-store',
      });
      
      if (!response.ok) {
        return { serviceOnline: false, browserRunning: false };
      }
      
      const data = await response.json();
      return { 
        serviceOnline: true, 
        browserRunning: data.browser?.running === true 
      };
    } catch (err) {
      console.error('Browser service inacess?vel:', err);
      return { serviceOnline: false, browserRunning: false };
    }
  };

  // Inicializa ao abrir modal - SEM dependencias que causem loop
  useEffect(() => {
    if (!isOpen) {
      hasInitialized.current = false;
      return;
    }

    // Evita re-inicializacao se ja foi feita
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const initialize = async () => {
      setStep('loading');
      setError(null);
      setOtp('');
      setSenha('');

      // 1. Verifica se o Browser service esta acessivel e se o Puppeteer esta rodando
      const browserBase = await resolveBrowserApi();
      const { serviceOnline, browserRunning } = await checkBrowserService(browserBase);

      if (!serviceOnline) {
        setStep('browser_offline');
        return;
      }

      // 2. Busca credenciais salvas
      await fetchSavedCredentials();

      // 3. Verifica se ja esta logado
      const authStatus = await checkAuthStatus();

      if (authStatus === 'LOGADO') {
        // Ja esta conectado, fecha o modal
        onSuccess();
        handleClose();
        return;
      }

      if (authStatus === 'AGUARDANDO_OTP') {
        setStep('otp');
        return;
      }

      setStep('credentials');
    };

    initialize();
  }, [isOpen]); // Apenas isOpen como depend?ncia

  // Detecta mudanca nas credenciais
  useEffect(() => {
    if (savedCredentials) {
      const emailChanged = email !== savedCredentials.email;
      const passwordChanged = !useSavedPassword && senha.length > 0;
      setCredentialsChanged(emailChanged || passwordChanged);
    }
  }, [email, senha, useSavedPassword, savedCredentials]);

  // Inicia o browser via Manager API
  const startBrowserViaManager = async (): Promise<boolean> => {
    try {
      const response = await fetch(`${CORE_API}/manager/tenants/${encodeURIComponent(resolvedTenant)}/browser/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ 
          headless: defaultHeadless
        })
      });
      
      if (!response.ok) {
        throw new Error('N?o foi poss?vel iniciar o servi?o');
      }
      
      await new Promise(r => setTimeout(r, 3000));
      return true;
    } catch (err) {
      console.error('Erro ao iniciar browser via Manager:', err);
      return false;
    }
  };

  // Handler para iniciar o browser (usado no step browser_offline)
  const handleStartBrowser = async () => {
    setStep('starting_browser');
    setError(null);
    
    try {
      // 1. Tenta iniciar o servi?o via Manager (PM2)
      const started = await startBrowserViaManager();
      
      if (!started) {
        throw new Error('Falha ao iniciar via Manager');
      }
      
      // 2. Aguarda o servi?o ficar online (pode demorar na primeira vez)
      await new Promise(r => setTimeout(r, 5000));
      
      // 3. Verifica se o servi?o respondeu (com retry)
      let { serviceOnline, browserRunning } = await checkBrowserService();
      
      // Se n?o respondeu, tenta mais uma vez
      if (!serviceOnline) {
        await new Promise(r => setTimeout(r, 3000));
        const retry = await checkBrowserService();
        serviceOnline = retry.serviceOnline;
        browserRunning = retry.browserRunning;
      }
      
      if (!serviceOnline) {
        throw new Error('Servi?o Browser n?o respondeu');
      }
      
      // 4. Inicia o Puppeteer com janela vis?vel (se ainda n?o estiver rodando)
      if (!browserRunning) {
        await startPuppeteer();
        
        // 5. Aguarda e verifica se o Puppeteer est? rodando
        await new Promise(r => setTimeout(r, 3000));
        const status = await checkBrowserService();
        
        if (!status.browserRunning) {
          throw new Error('Puppeteer n?o iniciou');
        }
      }
      
      // 6. Busca credenciais e vai para step de login
      await fetchSavedCredentials();
      const authStatus = await checkAuthStatus();
      
      if (authStatus === 'LOGADO') {
        // J? est? conectado, fecha o modal
        onSuccess();
        handleClose();
        return;
      }
      
      setStep('credentials');
    } catch (err: any) {
      console.error('Erro ao iniciar browser:', err);
      setError('N?o foi poss?vel iniciar o Browser. Verifique se o Manager est? rodando (pnpm run dev:manager)');
      setStep('browser_offline');
    }
  };

  // Inicia o Puppeteer browser diretamente
  const startPuppeteer = async () => {
    try {
      await fetch(`${CORE_API}/manager/tenants/${encodeURIComponent(resolvedTenant)}/browser/start`, { 
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ headless: defaultHeadless })
      });
      await new Promise(r => setTimeout(r, 2000));
      return true;
    } catch (err) {
      console.error('Erro ao iniciar Puppeteer:', err);
      return false;
    }
  };

  // Salva credenciais alteradas (armazenamento agora gerenciado pelo Manager)
  const saveCredentials = async () => {
    if (!credentialsChanged) return true;
    try {
      const payload: Record<string, string> = {};
      payload.url = DEFAULT_CVCRM_URL;
      if (email) payload.email = email;
      if (!useSavedPassword && senha) payload.password = senha;
      const response = await fetch(`${CORE_API}/manager/tenants/${encodeURIComponent(resolvedTenant)}/cvcrm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.error || 'Falha ao salvar credenciais');
      }
      return true;
    } catch (err) {
      console.error('Erro ao salvar credenciais CVCRM:', err);
      throw err;
    }
  };

  // Handle login completo
  const handleLogin = async () => {
    if (!email) {
      setError('Preencha o email');
      return;
    }

    const hasSavedPassword = Boolean(savedCredentials?.senha_configurada);
    if ((!hasSavedPassword && !senha) || (!useSavedPassword && !senha)) {
      setError('Preencha a senha');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // 1. Verifica se browser est? rodando
      let browserStatus = await checkAuthStatus();
      
      if (!browserStatus) {
        const started = await startBrowserViaManager();
        if (!started) {
          throw new Error('N?o foi poss?vel iniciar o servi?o. Execute: pnpm run dev:manager');
        }
      }
      
      // 2. Verifica se j? est? logado
      browserStatus = await checkAuthStatus();
      if (browserStatus === 'LOGADO') {
        // J? estava conectado, fecha o modal
        onSuccess();
        handleClose();
        return;
      }
      
      // 3. Inicia o Puppeteer se necess?rio
      await startPuppeteer();
      
      // 4. Verifica novamente (pode ter sess?o salva)
      browserStatus = await checkAuthStatus();
      if (browserStatus === 'LOGADO') {
        // Sess?o restaurada, fecha o modal
        onSuccess();
        handleClose();
        return;
      }

      // 5. Salva credenciais se mudaram
      if (credentialsChanged) {
        await saveCredentials();
      }
      
      // 6. Faz login
      const loginBody: Record<string, string> = { email };
      
      if (!useSavedPassword && senha) {
        loginBody.senha = senha;
      }
      
      if (otp && otp.length === 6) {
        loginBody.otp = otp;
      }

      const response = await fetch(`${browserApiBase}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(loginBody)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao fazer login');
      }

      // Verifica sucesso primeiro (pode vir de sess?o j? ativa)
      if (data.success === true || data.status === 'LOGADO') {
        // Sucesso! Fecha modal e notifica
        onSuccess();
        handleClose();
      } else if (data.status === 'AGUARDANDO_OTP') {
        setStep('otp');
        setOtp(''); // Limpa para digitar novo
      } else if (data.status === 'REQUER_TROCA_SENHA') {
        setError('O CVCRM exige troca de senha. Fa?a isso manualmente primeiro.');
      } else if (data.status === 'ERRO_RECAPTCHA') {
        // reCAPTCHA detectado - reinicia o browser para tentar limpar
        setError('reCAPTCHA ativado. Reiniciando browser...');
        await restartBrowserToResetCaptcha();
      } else {
        setError(data.message || 'Erro desconhecido');
      }
    } catch (err: any) {
      // Traduz mensagens de erro comuns
      const errorMsg = translateError(err.message || 'Falha na conex?o');
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // Traduz mensagens de erro para portugu?s
  const translateError = (msg: string): string => {
    const translations: Record<string, string> = {
      'Failed to fetch': 'N?o foi poss?vel conectar ao servi?o. Verifique se o Browser est? rodando.',
      'NetworkError': 'Erro de rede. Verifique sua conex?o.',
      'Load failed': 'Falha ao carregar. Tente novamente.',
      'Network request failed': 'Requisi??o de rede falhou. Verifique sua conex?o.',
    };
    
    for (const [en, pt] of Object.entries(translations)) {
      if (msg.includes(en)) return pt;
    }
    return msg;
  };

  // Reinicia o browser para tentar limpar o reCAPTCHA
  const restartBrowserToResetCaptcha = async () => {
    try {
      // Para o browser
      await fetch(`${CORE_API}/manager/tenants/${encodeURIComponent(resolvedTenant)}/browser/stop`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() } }).catch(() => {});
      await new Promise(r => setTimeout(r, 2000));
      
      // Reinicia via Manager
      await fetch(`${CORE_API}/manager/tenants/${encodeURIComponent(resolvedTenant)}/restart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ services: ['browser'] })
      }).catch(() => {});
      
      await new Promise(r => setTimeout(r, 3000));
      
      setError('Browser reiniciado. Tente novamente em alguns segundos.');
    } catch (e) {
      setError('reCAPTCHA ativado. Aguarde alguns minutos e tente novamente.');
    }
  };

  // Handle OTP submit
  const handleOtpSubmit = async () => {
    if (!otp || otp.length !== 6) {
      setError('Digite o c?digo de 6 d?gitos');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${browserApiBase}/login/otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ otp })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'C?digo inv?lido');
      }

      // Verifica sucesso
      if (data.success === true || data.status === 'LOGADO') {
        // Sucesso! Fecha modal e notifica
        onSuccess();
        handleClose();
      } else {
        setError(data.message || 'C?digo inv?lido');
      }
    } catch (err: any) {
      const errorMsg = translateError(err.message || 'Falha ao validar OTP');
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // Fecha o modal e reseta
  const handleClose = () => {
    hasInitialized.current = false;
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className={`
        w-full max-w-md rounded-2xl shadow-2xl transform transition-all
        ${darkMode ? 'bg-vision-darkGreen text-white border border-vision-border' : 'bg-white text-gray-900'}
      `}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${darkMode ? 'border-vision-border' : 'border-gray-100'}`}>
          <h3 className="text-xl font-bold flex items-center gap-2">
            <Shield size={24} className="text-blue-500" />
            {step === 'loading' ? 'Carregando...' : 
             step === 'browser_offline' ? 'Browser Offline' :
             step === 'starting_browser' ? 'Iniciando...' :
             step === 'otp' ? 'C?digo OTP' : 
             'Conectar CVCRM'}
          </h3>
          <button 
            onClick={handleClose}
            disabled={isLoading}
            className={`p-1 rounded-full transition-colors ${darkMode ? 'hover:bg-vision-surface' : 'hover:bg-gray-100'} disabled:opacity-50`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          
          {/* Error Alert */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-red-700 text-sm">
              <AlertTriangle size={18} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Step: Loading */}
          {step === 'loading' && (
            <div className="py-8 text-center">
              <Loader2 size={40} className="animate-spin mx-auto mb-4 text-blue-500" />
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Verificando configura??o...
              </p>
            </div>
          )}

          {/* Step: Browser Offline */}
          {step === 'browser_offline' && (
            <div className="py-6 text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                darkMode ? 'bg-orange-900/30' : 'bg-orange-50'
              }`}>
                <AlertTriangle size={32} className="text-orange-500" />
              </div>
              <h4 className={`font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Browser n?o est? rodando
              </h4>
              <p className={`text-sm mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                O servi?o Browser precisa estar ativo para conectar ao CVCRM.
              </p>
              
              <button
                onClick={handleStartBrowser}
                className="w-full py-3 rounded-lg font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <Shield size={18} />
                Iniciar Browser
              </button>
              
              <p className={`text-xs mt-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Ou execute manualmente: <code className="font-mono bg-gray-100 dark:bg-gray-800 px-1 rounded">pnpm run dev:browser</code>
              </p>
            </div>
          )}

          {/* Step: Starting Browser */}
          {step === 'starting_browser' && (
            <div className="py-8 text-center">
              <Loader2 size={40} className="animate-spin mx-auto mb-4 text-blue-500" />
              <h4 className={`font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Iniciando Browser...
              </h4>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Aguarde enquanto o servi?o ? iniciado.
              </p>
              <p className={`text-xs mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Uma janela do navegador deve abrir em breve.
              </p>
            </div>
          )}

          {/* Step: Credentials */}
          {step === 'credentials' && (
            <div className="space-y-4">
              {/* Credenciais salvas badge */}
              {savedCredentials?.secureConfig && (
                <div className={`text-xs px-3 py-2 rounded-lg flex items-center gap-2 ${
                  darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700'
                }`}>
                  <Shield size={14} />
                  Credenciais criptografadas encontradas
                </div>
              )}
              
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {savedCredentials?.email 
                  ? 'Confirme as credenciais ou altere se necess?rio.'
                  : 'Digite suas credenciais do CVCRM para conectar.'}
              </p>

              {/* Email */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu.email@cvcrm.com"
                  disabled={isLoading}
                  className={`w-full px-4 py-2.5 rounded-lg border transition-colors ${
                    darkMode 
                      ? 'bg-vision-surface border-vision-border text-white placeholder:text-gray-500 focus:border-blue-500' 
                      : 'bg-white border-gray-200 focus:border-blue-500'
                  } disabled:opacity-50`}
                />
              </div>

              {/* Senha */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Senha
                  </label>
                  {savedCredentials?.senha_configurada && (
                    <button
                      type="button"
                      onClick={() => {
                        setUseSavedPassword(!useSavedPassword);
                        if (!useSavedPassword) setSenha('');
                      }}
                      className={`text-xs flex items-center gap-1 ${
                        darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
                      }`}
                    >
                      <RefreshCw size={12} />
                      {useSavedPassword ? 'Alterar senha' : 'Usar senha salva'}
                    </button>
                  )}
                </div>
                
                {useSavedPassword && savedCredentials?.senha_configurada ? (
                  <div className={`w-full px-4 py-2.5 rounded-lg border ${
                    darkMode ? 'bg-vision-surface border-vision-border text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500'
                  }`}>
                    ???????????? (senha salva)
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={senha}
                      onChange={(e) => {
                        setSenha(e.target.value);
                        if (savedCredentials?.senha_configurada) {
                          setUseSavedPassword(false);
                        }
                      }}
                      placeholder="Digite sua senha"
                      disabled={isLoading}
                      className={`w-full px-4 py-2.5 pr-10 rounded-lg border transition-colors ${
                        darkMode 
                          ? 'bg-vision-surface border-vision-border text-white placeholder:text-gray-500 focus:border-blue-500' 
                          : 'bg-white border-gray-200 focus:border-blue-500'
                      } disabled:opacity-50`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={`absolute right-3 top-1/2 -translate-y-1/2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                )}
              </div>

              {/* OTP inline (opcional) - 6 boxes */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  C?digo OTP <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>(se necess?rio)</span>
                </label>
                <OtpInput
                  value={otp}
                  onChange={setOtp}
                  onSubmit={otp.length === 6 ? handleLogin : undefined}
                  darkMode={darkMode}
                  disabled={isLoading}
                />
              </div>

              {/* Credenciais alteradas info */}
              {credentialsChanged && (
                <div className={`text-xs px-3 py-2 rounded-lg flex items-center gap-2 ${
                  darkMode ? 'bg-yellow-900/30 text-yellow-300' : 'bg-yellow-50 text-yellow-700'
                }`}>
                  <Save size={14} />
                  As credenciais ser?o atualizadas ao conectar
                </div>
              )}

              <button
                onClick={handleLogin}
                disabled={isLoading || !email}
                className="w-full py-3 rounded-lg font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Conectando...
                  </>
                ) : (
                  <>
                    <Shield size={18} />
                    Conectar
                  </>
                )}
              </button>
            </div>
          )}

          {/* Step: OTP */}
          {step === 'otp' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                  darkMode ? 'bg-blue-900/30' : 'bg-blue-50'
                }`}>
                  <KeyRound size={32} className="text-blue-500" />
                </div>
                <h4 className={`font-bold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Verifica??o em duas etapas
                </h4>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Digite o c?digo de 6 d?gitos do seu autenticador
                </p>
              </div>

              <OtpInput
                value={otp}
                onChange={setOtp}
                onSubmit={handleOtpSubmit}
                darkMode={darkMode}
                disabled={isLoading}
              />

              <button
                onClick={handleOtpSubmit}
                disabled={isLoading || otp.length !== 6}
                className="w-full py-3 rounded-lg font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Validando...
                  </>
                ) : (
                  <>
                    <CheckCircle size={18} />
                    Confirmar
                  </>
                )}
              </button>

              <button
                onClick={() => { setStep('credentials'); setOtp(''); }}
                disabled={isLoading}
                className={`w-full py-2 text-sm ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'} disabled:opacity-50`}
              >
                ? Voltar
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};








